// What a run DELIVERED (arc 101 slice 06) — `GET /v1/tasks/:id/report`, the REST
// twin of the MCP `task result` verb over the same projection, so the two doors
// cannot answer differently.
//
// A TASK THAT HAS DELIVERED NOTHING IS A 200 WITH `report: null`, NOT A 404. The
// 404 is reserved for a task this account does not have, so the view can tell
// "nothing yet" from "not yours" — which is the whole reason the route draws that
// line, and why nothing here turns an empty report into an error.
//
// The three sibling blocks (`raw_tasks_filed`, `blocker`, `devops`) are typed
// `unknown` at their leaves ON PURPOSE: their shapes are owned by the runner lane
// that emits them (`apps/runner/src/modes/worker/devops/filing.ts`), apps never
// import apps, and a hand-copied mirror here would be a second schema to drift.
// The readers below take what they can prove and hand the rest back verbatim.
//
// THE ENVELOPE AROUND THEM IS PARSED, THOUGH. `apiFetch<TaskReportView>` was a
// CAST: a body missing `report_missing`, or carrying a `report` with no
// `markdown`, reached the presenter and failed there — blaming the render for a
// wire fault. The schema below validates every field this app decides on and
// leaves the opaque ones as opaque records, so an invalid body becomes the
// screen's own error state naming the field (`./api-errors.ts`, `parseBody`).
//
// It lives HERE and not in `@developerz/domain` because domain does not own this
// projection — `projectTaskResult` (`packages/mcp/src/army/task-result.ts`) does,
// and it types the same leaves `Record<string, unknown>` for the same reason. A
// third copy in domain would be a second contract for one capability, which is
// the drift this comment is warning about.

import { ReportArtifactSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** An opaque runner-owned payload: an object, with leaves this app does not decide. */
const OpaqueRecordSchema = z.record(z.string(), z.unknown());

/** The report body, with the `source` that says where it came from. */
const TaskReportBodySchema = z.object({
  markdown: z.string(),
  /** References, never inlined bytes (`ReportArtifactSchema`, validated server-side). */
  artifacts: z.array(ReportArtifactSchema),
  /** `report_md` | `final_turn` | `none`, or `unknown` on a row predating the field. */
  source: z.string(),
});
export type TaskReportBody = z.infer<typeof TaskReportBodySchema>;

/** `task.result`'s wire body. */
export const TaskReportViewSchema = z.object({
  task_id: z.string(),
  /** The run the report is read from — the latest that left rows; null when none has. */
  run_id: z.string().nullable(),
  /** Distinct runs that left deliverable rows; `> 1` means the task was retried. */
  runs_seen: z.number().int().nonnegative(),
  report: TaskReportBodySchema.nullable(),
  /** The run saying it produced no deliverable and failed for it. */
  report_missing: z.boolean(),
  raw_tasks_filed: z.array(OpaqueRecordSchema),
  blocker: OpaqueRecordSchema.nullable(),
  devops: OpaqueRecordSchema.nullable(),
});
export type TaskReportView = z.infer<typeof TaskReportViewSchema>;

export const TASK_REPORT_QUERY_KEY = 'task-report';

/** One task's deliverable. Idle without a session or a task id. */
export function useTaskReport(taskId: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<TaskReportView>(() => ({
    queryKey: [TASK_REPORT_QUERY_KEY, taskId(), token, activeAccountId()],
    enabled: token !== null && taskId() !== '',
    queryFn: () =>
      apiFetch(`/v1/tasks/${encodeURIComponent(taskId())}/report`, token, undefined, {
        schema: TaskReportViewSchema,
      }),
  }));
}

/**
 * The i18n key naming where the report came from. TOTAL over the wire's `string`:
 * a source this app has no copy for reads as "not recorded" rather than `⟦key⟧`.
 */
export function reportSourceKey(source: string): string {
  if (source === 'report_md') return 'devops_report.source_report_md';
  if (source === 'final_turn') return 'devops_report.source_final_turn';
  if (source === 'none') return 'devops_report.source_none';
  return 'devops_report.source_unknown';
}

/** A field of an opaque payload, when it is a non-empty string. Null otherwise. */
export function reportText(payload: Record<string, unknown> | null, field: string): string | null {
  const value = payload?.[field];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * What a blocker asked for, as a VERDICT — three states, never a trimmed list.
 *
 * - `none`: the blocker named no access. Nothing to show.
 * - `list`: every member read as a non-empty string. Show them.
 * - `malformed`: the field is present and this app cannot read it. Say so.
 *
 * The third member is why this is not a `string[]` filter. `required_access` is
 * the ASK a human has to grant, and the producer validates it as an array of
 * non-empty strings (`DevopsBlockerSchema`, `devops/filing.ts`), so anything else
 * reaching here is a row that lane did not write. Filtering the unreadable members
 * out would have rendered a SHORTER ask — or an empty one — under the same heading,
 * and nothing on the page would have said a requirement went missing.
 */
export type BlockerAccess =
  | { readonly kind: 'none' }
  | { readonly kind: 'list'; readonly needs: readonly string[] }
  | { readonly kind: 'malformed' };

/** Read `required_access` off a blocker payload. Total over any JSON value. */
export function blockerAccess(payload: Record<string, unknown> | null): BlockerAccess {
  const value = payload?.['required_access'];
  if (value === undefined || value === null) return { kind: 'none' };
  if (!Array.isArray(value)) return { kind: 'malformed' };
  if (value.length === 0) return { kind: 'none' };
  const needs = value.filter((one): one is string => typeof one === 'string' && one.length > 0);
  return needs.length === value.length ? { kind: 'list', needs } : { kind: 'malformed' };
}
